import React, { useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ScriptableContext,
  TooltipItem,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Checkbox } from "@/components/ui/checkbox";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface TimelineDataPoint {
  name: string;
  gtm: number;
  gna: number;
  tech: number;
  exec: number;
  total: number;
}

interface ChartContext {
  dataIndex: number;
  parsed: {
    y: number;
  };
  dataset: {
    label: string;
  };
}

interface HiringChartProps {
  timelineData: TimelineDataPoint[];
  goalAchievedMonths: { [key: string]: number };
  timelineMonths: number;
  totalHiresNeeded: { [key: string]: number };
}

export const HiringChart = ({ timelineData, goalAchievedMonths, timelineMonths, totalHiresNeeded }: HiringChartProps) => {
  const [hoveredDataset, setHoveredDataset] = useState<string | null>(null);
  const [hiddenDatasets, setHiddenDatasets] = useState<Set<string>>(new Set());

  const areas = [
    { key: "tech", label: "Tech & Product", color: "#F59E0B" },
    { key: "gtm", label: "Go-to-market", color: "#4F46E5" },
    { key: "gna", label: "G&A", color: "#10B981" },
    { key: "exec", label: "Executive", color: "#EF4444" },
  ];

  // Calculate which departments have non-zero values once
  const departmentsWithValues = areas.filter(area => 
    (totalHiresNeeded[area.key] || 0) > 0
  );

  const options = {
    responsive: true,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function(context: TooltipItem<"line">) {
            return `${context.dataset.label}: ${Math.round(context.parsed.y)} hires`;
          }
        },
        itemSort: function(a: TooltipItem<"line">, b: TooltipItem<"line">) {
          return b.parsed.y - a.parsed.y;
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        title: {
          display: true,
          text: 'Months'
        },
        ticks: {
          callback: function(value: number | string) {
            return `Month ${value}`;
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: '#f0f0f0',
        },
        title: {
          display: true,
          text: 'Cumulative New Hires'
        }
      },
    },
    animation: {
      duration: 750,
      easing: 'easeInOutQuart' as const,
    },
  };

  const toggleDataset = (label: string) => {
    const newHiddenDatasets = new Set(hiddenDatasets);
    if (newHiddenDatasets.has(label)) {
      newHiddenDatasets.delete(label);
    } else {
      newHiddenDatasets.add(label);
    }
    setHiddenDatasets(newHiddenDatasets);
    console.log('Toggle dataset:', label, 'Hidden datasets:', Array.from(newHiddenDatasets));
  };

  const data = {
    labels: ['0', ...timelineData.map((_, index) => (index + 1).toString())],
    datasets: departmentsWithValues
      .filter(area => !hiddenDatasets.has(area.label))
      .map(area => {
        const monthlyHires = timelineData.map(d => {
          const value = typeof d[area.key as keyof typeof d] === 'string' 
            ? parseFloat(d[area.key as keyof typeof d] as string) 
            : (d[area.key as keyof typeof d] as number);
          return value;
        });

        const goalMonth = goalAchievedMonths[area.key] || timelineMonths;
        
        // Calculate cumulative hires up to the goal month only
        const cumulativeHires = [0];
        let total = 0;
        for (let i = 0; i < monthlyHires.length; i++) {
          if (i < goalMonth) {
            total += Number.isNaN(monthlyHires[i]) ? 0 : monthlyHires[i];
            cumulativeHires.push(total);
          }
        }

        console.log(`${area.label} visualization details:`, {
          goalMonth,
          totalMonths: timelineMonths,
          cumulativeHires,
          willShowDottedLine: false,
          accentBubbleMonth: goalMonth
        });
        
        return {
          label: area.label,
          data: cumulativeHires,
          borderColor: area.color,
          backgroundColor: area.color,
          tension: 0.4,
          fill: false,
          pointRadius: (ctx: ScriptableContext<"line"> | undefined) => {
            if (!ctx || !ctx.dataIndex) return 3;
            return ctx.dataIndex === goalMonth ? 6 : 3;
          },
          pointHoverRadius: 8,
          borderWidth: hoveredDataset === area.label ? 3 : 2,
          opacity: hoveredDataset === null ? 1 : hoveredDataset === area.label ? 1 : 0.3,
        };
      })
      .sort((a, b) => {
        const aLastValue = a.data[a.data.length - 1] || 0;
        const bLastValue = b.data[b.data.length - 1] || 0;
        return bLastValue - aLastValue;
      }),
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-center items-center w-full h-[400px]">
        <div className="w-full max-w-4xl">
          <Line options={options} data={data} />
        </div>
      </div>
      <div className="flex flex-wrap justify-center gap-6">
        {departmentsWithValues.map((area) => (
          <div
            key={area.key}
            className="flex items-center space-x-2"
            onMouseEnter={() => {
              console.log('Legend hover:', area.label);
              setHoveredDataset(area.label);
            }}
            onMouseLeave={() => {
              console.log('Legend leave');
              setHoveredDataset(null);
            }}
          >
            <Checkbox
              id={area.key}
              checked={!hiddenDatasets.has(area.label)}
              onCheckedChange={() => toggleDataset(area.label)}
              className="border-2"
              style={{
                borderColor: area.color,
                backgroundColor: !hiddenDatasets.has(area.label) ? area.color : 'transparent'
              }}
            />
            <label
              htmlFor={area.key}
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {area.label}
            </label>
          </div>
        ))}
      </div>
    </div>
  );
};